import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback } from 'react';
import { UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useAnalyticsPropertiesContext } from './analytics-properties-context';
import { AI_MATE_ATTRIBUTES } from './analytics-properties-context/types';
import { ANALYTICS_CHANNEL } from './constants';
import { getAttributesFromContexts } from './utils';
var isTownSquareEvent = function isTownSquareEvent() {
  return ['home.atlassian.com', 'team.atlassian.com'].some(function (host) {
    var _window$location;
    return (_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.host.includes(host);
  });
};
var DEFAULT_TAGS = ['atlaskit', 'aiMate'];

/**
 * A wrapper around the analytics-next hook to send analytics events to the ai mate channel
 */
export var useAnalytics = function useAnalytics() {
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useAnalyticsProperti = useAnalyticsPropertiesContext(),
    propertiesRef = _useAnalyticsProperti.propertiesRef;

  // TODO: Improve this functionality, potentially with a higher order provider for tags or similar
  // This is a temporary solution to add the 'townsquare' tag to operational events so Experience Tracker works in Atlas
  var maybeAtlasOperationalTags = useCallback(function (inputPayload) {
    var payload = inputPayload instanceof UIAnalyticsEvent ? inputPayload.payload : inputPayload;
    if (payload.actionSubject === 'ui' && payload.eventType === 'operational' && isTownSquareEvent()) {
      return ['townsquare'];
    }
    return [];
  }, []);
  var getDefaultAttributes = useCallback(function () {
    var _ref;
    var _propertiesRef$curren = propertiesRef.current,
      agentCreatorType = _propertiesRef$curren.agentCreatorType,
      agentId = _propertiesRef$curren.agentId,
      agentVisibility = _propertiesRef$curren.agentVisibility,
      agentExternalConfigReference = _propertiesRef$curren.agentExternalConfigReference,
      agentActions = _propertiesRef$curren.agentActions,
      agentIsDefault = _propertiesRef$curren.agentIsDefault,
      conversationChannelId = _propertiesRef$curren.conversationChannelId,
      experienceId = _propertiesRef$curren.experienceId,
      fullScreen = _propertiesRef$curren.fullScreen,
      product = _propertiesRef$curren.product,
      productHeader = _propertiesRef$curren.productHeader;
    return _ref = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_ref, AI_MATE_ATTRIBUTES.agentCreatorType, agentCreatorType), AI_MATE_ATTRIBUTES.agentId, agentId), AI_MATE_ATTRIBUTES.agentVisibility, agentVisibility), AI_MATE_ATTRIBUTES.conversationChannelId, conversationChannelId), AI_MATE_ATTRIBUTES.experienceId, experienceId), AI_MATE_ATTRIBUTES.fullScreen, fullScreen), AI_MATE_ATTRIBUTES.product, product), AI_MATE_ATTRIBUTES.productHeader, productHeader), AI_MATE_ATTRIBUTES.singleInstrumentationID, conversationChannelId), AI_MATE_ATTRIBUTES.agentExternalConfigReference, agentExternalConfigReference), _defineProperty(_defineProperty(_ref, AI_MATE_ATTRIBUTES.agentActions, agentActions), AI_MATE_ATTRIBUTES.agentIsDefault, agentIsDefault);
  }, [propertiesRef]);
  var trackAIMAUEvent = useCallback(function (payload) {
    // Handle tags
    var tags = [].concat(DEFAULT_TAGS);
    if (isTownSquareEvent()) {
      tags.push('townsquare');
    }
    var source = payload.source,
      subjectId = payload.subjectId,
      proactive = payload.proactive,
      userGenerated = payload.userGenerated,
      messageId = payload.messageId,
      messageActions = payload.messageActions,
      singleInstrumentationID = payload.singleInstrumentationID,
      timestamp = payload.timestamp,
      citedProductList = payload.citedProductList,
      citedProduct = payload.citedProduct,
      invokedPlugins = payload.invokedPlugins;
    var attributes = _objectSpread(_objectSpread({}, getDefaultAttributes()), {}, {
      isAIFeature: 1,
      proactiveAIGenerated: proactive ? 1 : 0,
      userGeneratedAI: userGenerated ? 1 : 0,
      aiFeatureName: 'aiChat',
      citedProductList: citedProductList,
      citedProduct: citedProduct
    });
    if (messageId) {
      attributes.messageId = messageId;
    }
    if (messageActions) {
      attributes.messageActions = messageActions;
    }
    if (timestamp) {
      attributes.timestamp = timestamp;
    }
    if (singleInstrumentationID) {
      attributes.singleInstrumentationID = singleInstrumentationID;
    }
    if (invokedPlugins) {
      attributes.invokedPlugins = invokedPlugins;
    }

    // Core AI MAU payload
    var eventPayload = {
      eventType: 'track',
      tags: tags,
      source: source,
      actionSubjectId: subjectId,
      attributes: attributes
    };

    // Handle specific AI MAU events
    switch (payload.type) {
      case 'initiated':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiInteraction',
          action: 'initiated'
        });
        break;
      case 'viewed':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiResult',
          action: 'viewed'
        });
        break;
      case 'error':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiResult',
          action: 'error'
        });
        break;
      case 'dismissed':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiInteraction',
          action: 'dismissed'
        });
        break;
      case 'actioned':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiResult',
          action: 'actioned',
          attributes: _objectSpread(_objectSpread({}, eventPayload.attributes), {}, {
            aiResultAction: payload.actionResult
          })
        });
        break;
      case 'feedback':
        eventPayload = _objectSpread(_objectSpread({}, eventPayload), {}, {
          actionSubject: 'aiFeedback',
          action: 'submitted',
          attributes: _objectSpread(_objectSpread({}, eventPayload.attributes), {}, {
            aiFeedbackResult: payload.feedbackResult
          })
        });
        break;
    }

    // Build event and apply context attributes
    var event = createAnalyticsEvent(eventPayload);
    var aiMateContextAttributes = getAttributesFromContexts(event.context);

    // Fire
    event.update({
      attributes: _objectSpread(_objectSpread({}, event.payload.attributes), aiMateContextAttributes)
    }).fire(ANALYTICS_CHANNEL);
  }, [createAnalyticsEvent, getDefaultAttributes]);
  var sendAnalyticsEvent = useCallback(function (payload, options) {
    var extraTags = options !== null && options !== void 0 && options.removeDefaultTags ? [] : DEFAULT_TAGS;
    var allTags = Array.from(new Set([].concat(extraTags, _toConsumableArray(payload.tags || []), _toConsumableArray(maybeAtlasOperationalTags(payload)))));
    var defaultAttributes = getDefaultAttributes();
    if (payload instanceof UIAnalyticsEvent) {
      var aiMateContextAttributes = getAttributesFromContexts(payload.context);
      payload.update(_objectSpread(_objectSpread({}, payload.payload), {}, {
        tags: allTags,
        attributes: _objectSpread(_objectSpread(_objectSpread({}, defaultAttributes), payload.payload.attributes), aiMateContextAttributes)
      })).fire(ANALYTICS_CHANNEL);
    } else {
      var event = createAnalyticsEvent(_objectSpread(_objectSpread({}, payload), {}, {
        tags: allTags,
        attributes: _objectSpread(_objectSpread({}, defaultAttributes), payload.attributes)
      }));
      var _aiMateContextAttributes = getAttributesFromContexts(event.context);
      event.update({
        attributes: _objectSpread(_objectSpread({}, event.payload.attributes), _aiMateContextAttributes)
      }).fire(ANALYTICS_CHANNEL);
    }
  }, [createAnalyticsEvent, maybeAtlasOperationalTags, getDefaultAttributes]);
  return {
    sendAnalyticsEvent: sendAnalyticsEvent,
    trackAIMAUEvent: trackAIMAUEvent
  };
};